// @ts-nocheck
import type { Question } from '../types';
import { 
  INSIGHT_TOPICS, 
  HAZARD_TOPICS, 
  KNOWLEDGE_TOPICS, 
  PRACTICE_TEST_CONFIG,
  isImageOnlyTest,
  filterImageQuestions,
  CategoryPool,
  TopicPool,
  ArchivePool
} from './categoryStructure';

export interface UserProgress {
  topicId: string;
  questionsSeen: string[];
  correctAnswers: string[];
  incorrectAnswers: string[];
  masteryLevel: number; // 0-100
  lastTestDate: Date;
  weakAreas: string[];
}

export interface TestGenerationRequest {
  category: 'insight' | 'hazardPerception' | 'knowledge' | 'mixed';
  topicIds?: string[];
  userProgress: UserProgress[];
  testNumber: number;
  difficulty?: 'easy' | 'medium' | 'hard';
}

export interface GeneratedTest {
  id: string;
  questions: Question[];
  category: string;
  topics: string[];
  difficulty: string;
  estimatedTime: number; // minutes
  unseenQuestions: number;
  weakAreaQuestions: number;
}

export class QuestionSelector {
  private categoryPools: Map<string, CategoryPool> = new Map();
  private topicPools: Map<string, TopicPool> = new Map();
  private archivePools: Map<string, ArchivePool> = new Map();

  constructor(
    insightQuestions: Map<string, Question[]>,
    hazardQuestions: Map<string, Question[]>,
    knowledgeQuestions: Map<string, Question[]>,
    archiveQuestions: Map<string, Question[]>
  ) {
    this.initializePools(insightQuestions, hazardQuestions, knowledgeQuestions, archiveQuestions);
  }

  private initializePools(
    insight: Map<string, Question[]>,
    hazard: Map<string, Question[]>,
    knowledge: Map<string, Question[]>,
    archive: Map<string, Question[]>
  ) {
    // Initialize category pools with exactly 40 questions per topic
    this.initializeCategoryPool('insight', INSIGHT_TOPICS, insight, 240);
    this.initializeCategoryPool('hazardPerception', HAZARD_TOPICS, hazard, 180);
    this.initializeCategoryPool('knowledge', KNOWLEDGE_TOPICS, knowledge, 180);
    
    // Initialize archive pools
    archive.forEach((questions, topicId) => {
      this.archivePools.set(topicId, {
        id: topicId,
        name: topicId,
        questions,
        isArchived: true,
        rotationEnabled: topicId === 'realExamQuestions' // Prioritize real exam questions
      });
    });
  }

  private initializeCategoryPool(
    categoryId: string,
    topicIds: readonly string[],
    questionMap: Map<string, Question[]>,
    targetTotal: number
  ) {
    const topicPools: TopicPool[] = [];
    let questionsPerTopic = Math.floor(targetTotal / topicIds.length);
    let remainder = targetTotal % topicIds.length;

    topicIds.forEach((topicId, index) => {
      const questions = questionMap.get(topicId) || [];
      const topicQuestionCount = questionsPerTopic + (index < remainder ? 1 : 0);
      
      // Select best questions for this topic
      const selectedQuestions = this.selectBestQuestions(questions, topicQuestionCount);
      
      const topicPool: TopicPool = {
        id: topicId,
        name: this.formatTopicName(topicId),
        category: categoryId,
        questions: selectedQuestions,
        totalQuestions: selectedQuestions.length,
        difficulty: this.calculateDifficulty(selectedQuestions)
      };
      
      topicPools.push(topicPool);
      this.topicPools.set(topicId, topicPool);
    });

    const categoryPool: CategoryPool = {
      id: categoryId,
      name: this.formatCategoryName(categoryId),
      totalQuestions: targetTotal,
      topicPools
    };

    this.categoryPools.set(categoryId, categoryPool);
  }

  private selectBestQuestions(questions: Question[], count: number): Question[] {
    if (questions.length <= count) return questions;
    
    // Sort by quality metrics (real exam questions first, then by difficulty balance)
    return questions
      .sort((a, b) => {
        // Prioritize real exam questions
        const aIsReal = 'examDate' in a;
        const bIsReal = 'examDate' in b;
        if (aIsReal && !bIsReal) return -1;
        if (!aIsReal && bIsReal) return 1;
        
        // Then by subject diversity
        return a.subject.localeCompare(b.subject);
      })
      .slice(0, count);
  }

  private calculateDifficulty(questions: Question[]): 'easy' | 'medium' | 'hard' {
    // Simple difficulty calculation based on question properties
    const avgOptions = questions.reduce((sum, q) => sum + q.options.length, 0) / questions.length;
    const avgExplanationLength = questions.reduce((sum, q) => sum + q.explanation.length, 0) / questions.length;
    
    if (avgOptions <= 3 && avgExplanationLength <= 100) return 'easy';
    if (avgOptions <= 4 && avgExplanationLength <= 200) return 'medium';
    return 'hard';
  }

  private formatTopicName(topicId: string): string {
    return topicId
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  private formatCategoryName(categoryId: string): string {
    const names = {
      insight: 'Insight & Awareness',
      hazardPerception: 'Hazard Perception',
      knowledge: 'Traffic Knowledge'
    };
    return names[categoryId as keyof typeof names] || categoryId;
  }

  // MAIN TEST GENERATION METHOD
  generateTest(request: TestGenerationRequest): GeneratedTest {
    const { category, userProgress, testNumber: _testNumber } = request;
    
    let selectedQuestions: Question[] = [];
    let topics: string[] = [];
    
    if (category === 'mixed') {
      // Mixed category test - use category weights
      const insightCount = Math.floor(PRACTICE_TEST_CONFIG.questionsPerTest * PRACTICE_TEST_CONFIG.categoryWeights.insight);
      const hazardCount = Math.floor(PRACTICE_TEST_CONFIG.questionsPerTest * PRACTICE_TEST_CONFIG.categoryWeights.hazardPerception);
      const knowledgeCount = PRACTICE_TEST_CONFIG.questionsPerTest - insightCount - hazardCount;
      
      selectedQuestions = [
        ...this.selectQuestionsFromCategory('insight', insightCount, userProgress),
        ...this.selectQuestionsFromCategory('hazardPerception', hazardCount, userProgress),
        ...this.selectQuestionsFromCategory('knowledge', knowledgeCount, userProgress)
      ];
      
      topics = ['insight', 'hazardPerception', 'knowledge'];
    } else {
      // Single category test
      selectedQuestions = this.selectQuestionsFromCategory(category, PRACTICE_TEST_CONFIG.questionsPerTest, userProgress);
      
      // IMPORTANT: Apply image-only filter for sign tests (old structure)
      if (isImageOnlyTest(category)) {
        selectedQuestions = filterImageQuestions(selectedQuestions);
        
        // If not enough image questions, get more from the same category
        if (selectedQuestions.length < PRACTICE_TEST_CONFIG.questionsPerTest) {
          const additionalQuestions = this.selectQuestionsFromCategory(category, PRACTICE_TEST_CONFIG.questionsPerTest - selectedQuestions.length, userProgress);
          const additionalImageQuestions = filterImageQuestions(additionalQuestions);
          selectedQuestions = [...selectedQuestions, ...additionalImageQuestions];
        }
        
        // Use only available image questions (don't force 20 questions)
        // For sign tests, we use whatever image questions are available
        selectedQuestions = selectedQuestions.slice(0, selectedQuestions.length);
      }
      
      topics = [category];
    }

    // Calculate test metadata
    const unseenCount = selectedQuestions.filter(q => 
      !userProgress.some(p => p.questionsSeen.includes(q.id))
    ).length;

    const weakAreaCount = selectedQuestions.filter(q => 
      userProgress.some(p => p.weakAreas.includes(q.subject))
    ).length;

    return {
      id: `test-${category}-${Date.now()}`,
      questions: this.shuffleArray(selectedQuestions),
      category,
      topics,
      difficulty: 'mixed',
      estimatedTime: PRACTICE_TEST_CONFIG.questionsPerTest * 1.5, // 1.5 minutes per question
      unseenQuestions: unseenCount,
      weakAreaQuestions: weakAreaCount
    };
  }

  private selectQuestionsFromCategory(
    categoryId: string, 
    count: number, 
    userProgress: UserProgress[]
  ): Question[] {
    const categoryPool = this.categoryPools.get(categoryId);
    if (!categoryPool) return [];

    const allQuestions = categoryPool.topicPools.flatMap(topic => topic.questions);
    const seenQuestions = new Set(userProgress.flatMap(p => p.questionsSeen));
    const weakAreas = new Set(userProgress.flatMap(p => p.weakAreas));

    // Separate questions by priority
    const unseen = allQuestions.filter(q => !seenQuestions.has(q.id));
    const weakArea = allQuestions.filter(q => weakAreas.has(q.subject));
    const regular = allQuestions.filter(q => seenQuestions.has(q.id) && !weakAreas.has(q.subject));

    // Calculate selection counts
    const unseenCount = Math.floor(count * PRACTICE_TEST_CONFIG.unseenPriority);
    const weakAreaCount = Math.floor(count * PRACTICE_TEST_CONFIG.weakAreaWeight);
    const regularCount = count - unseenCount - weakAreaCount;

    // Select questions from each pool
    const selected = [
      ...this.selectRandomQuestions(unseen, unseenCount),
      ...this.selectRandomQuestions(weakArea, weakAreaCount),
      ...this.selectRandomQuestions(regular, regularCount)
    ];

    return selected.slice(0, count);
  }

  private selectRandomQuestions(questions: Question[], count: number): Question[] {
    const shuffled = this.shuffleArray([...questions]);
    return shuffled.slice(0, count);
  }

  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  // ARCHIVE ROTATION
  rotateArchiveQuestions(topicId: string, count: number): Question[] {
    const archivePool = this.archivePools.get(topicId);
    if (!archivePool || !archivePool.rotationEnabled) return [];

    return this.selectRandomQuestions(archivePool.questions, count);
  }

  // ANALYTICS
  getCategoryStats(): Map<string, any> {
    const stats = new Map();
    
    this.categoryPools.forEach((pool, categoryId) => {
      stats.set(categoryId, {
        totalQuestions: pool.totalQuestions,
        topicCount: pool.topicPools.length,
        avgQuestionsPerTopic: Math.round(pool.totalQuestions / pool.topicPools.length),
        difficultyDistribution: this.getDifficultyDistribution(pool.topicPools)
      });
    });

    return stats;
  }

  private getDifficultyDistribution(topicPools: TopicPool[]) {
    const distribution = { easy: 0, medium: 0, hard: 0 };
    topicPools.forEach(topic => {
      distribution[topic.difficulty]++;
    });
    return distribution;
  }
}
